import { getFieldOptions } from '@/entities/profile';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { useTranslate } from '@tolgee/react';
import { useEffect } from 'react';
import { RequiredMark, SelectField } from './fields';

/** Legacy step5 (public): studies, profession, marital situation. */
export interface SituationStepData {
  studyLevel?: number;
  profession: string;
  nodeBusiness?: number;
  isStudyLevelMatter?: number;
  maritalStatus?: number;
  children?: number;
  childrenCare?: number;
  livingPlace?: number;
  administrativeState?: number;
  peerPast?: number;
  familyValues?: number;
}

interface StepSituationProps {
  data: SituationStepData;
  onChange: (data: Partial<SituationStepData>) => void;
  onValidChange: (isValid: boolean) => void;
}

const isSet = (v: number | undefined) => v !== undefined && v !== null;

export function StepSituation({ data, onChange, onValidChange }: StepSituationProps) {
  const { t } = useTranslate();
  const needsChildrenCare = (data.children ?? 0) > 0;

  // Required per legacy NodeHumanInfoPublicType: studyLevel / nodeBusiness optional,
  // childrenCare only when the member has children.
  // biome-ignore lint/correctness/useExhaustiveDependencies: onValidChange is stable from parent
  useEffect(() => {
    onValidChange(
      data.profession.trim().length > 0 &&
        isSet(data.isStudyLevelMatter) &&
        isSet(data.maritalStatus) &&
        isSet(data.children) &&
        (!needsChildrenCare || isSet(data.childrenCare)) &&
        isSet(data.livingPlace) &&
        isSet(data.administrativeState) &&
        isSet(data.peerPast) &&
        isSet(data.familyValues),
    );
  }, [data, needsChildrenCare]);

  return (
    <div className="space-y-4">
      <SelectField
        id="studyLevel"
        label={t('profile.fields.studyLevel')}
        options={getFieldOptions(t, 'studyLevel')}
        value={data.studyLevel}
        onChange={(v) => onChange({ studyLevel: v })}
      />
      <div className="space-y-2">
        <Label htmlFor="profession">
          {t('profile.fields.profession')}
          <RequiredMark required />
        </Label>
        <Input
          id="profession"
          type="text"
          placeholder={t('signup.placeholders.profession')}
          value={data.profession}
          onChange={(e) => onChange({ profession: e.target.value })}
          required
        />
      </div>
      <SelectField
        id="nodeBusiness"
        label={t('profile.fields.nodeBusiness')}
        options={getFieldOptions(t, 'nodeBusiness')}
        value={data.nodeBusiness}
        onChange={(v) => onChange({ nodeBusiness: v })}
      />
      <SelectField
        id="isStudyLevelMatter"
        label={t('profile.fields.isStudyLevelMatter')}
        options={getFieldOptions(t, 'boolean')}
        value={data.isStudyLevelMatter}
        onChange={(v) => onChange({ isStudyLevelMatter: v })}
        required
      />
      <SelectField
        id="maritalStatus"
        label={t('profile.fields.maritalStatus')}
        options={getFieldOptions(t, 'maritalStatus')}
        value={data.maritalStatus}
        onChange={(v) => onChange({ maritalStatus: v })}
        required
      />
      <SelectField
        id="children"
        label={t('profile.fields.children')}
        options={getFieldOptions(t, 'children')}
        value={data.children}
        onChange={(v) =>
          onChange({ children: v, childrenCare: v > 0 ? data.childrenCare : undefined })
        }
        required
      />
      {needsChildrenCare && (
        <SelectField
          id="childrenCare"
          label={t('profile.fields.childrenCare')}
          options={getFieldOptions(t, 'childrenCare')}
          value={data.childrenCare}
          onChange={(v) => onChange({ childrenCare: v })}
          required
        />
      )}
      <SelectField
        id="livingPlace"
        label={t('profile.fields.livingPlace')}
        options={getFieldOptions(t, 'livingPlace')}
        value={data.livingPlace}
        onChange={(v) => onChange({ livingPlace: v })}
        required
      />
      <SelectField
        id="administrativeState"
        label={t('profile.fields.administrativeState')}
        options={getFieldOptions(t, 'administrativeState')}
        value={data.administrativeState}
        onChange={(v) => onChange({ administrativeState: v })}
        required
      />
      <SelectField
        id="peerPast"
        label={t('profile.fields.peerPast')}
        options={getFieldOptions(t, 'peerPast')}
        value={data.peerPast}
        onChange={(v) => onChange({ peerPast: v })}
        required
      />
      <SelectField
        id="familyValues"
        label={t('profile.fields.familyValues')}
        options={getFieldOptions(t, 'familyValues')}
        value={data.familyValues}
        onChange={(v) => onChange({ familyValues: v })}
        required
      />
      <p className="text-xs text-muted-foreground">{t('signup.hints.required')}</p>
    </div>
  );
}
